import { apiDelete, apiGet, apiPost, apiPut, buildQueryParams, type PageResult } from '~/utils/api'

export interface AffiliateApplication {
  id: string; status: string; applicantName: string; phone?: string | null
  channelTypes: string[]; channelLinks: string[]; audienceRegions: string[]
  promotionPlan: string; taxResidenceCountry: string; agreementVersion: string
  submittedAt?: string | null; decisionReasonCode?: string | null; applicantMessage?: string | null
}
export interface AffiliateApplicationInput {
  applicantName: string; phone?: string; channelTypes: string[]; channelLinks: string[]
  audienceRegions: string[]; promotionPlan: string; taxResidenceCountry: string
  agreementVersion: string; agreementAccepted: boolean
}
export interface AffiliateProfile {
  id: string; status: string; payoutEligibilityStatus: string; displayName: string
  referralCode: string; referralUrl: string; riskStatus: string; approvedAt: string
}
export interface AffiliateDashboard {
  validClicks30d: number; attributedOrders: number; paidOrders: number; conversionRate: number
  lockedCommission: number; availableBalance: number; payoutReserved: number; paidTotal: number
  reversedTotal: number; debt: number; currency: string; metricTimezone: string
}
export interface AffiliateProduct {
  id: string; title: string; slug: string; image?: string | null; salePrice: number
  commissionType: string; commissionValue: number; currency: string
  estimatedCommission: number; referralUrl: string
}
export interface AffiliateCommission {
  id: string; orderReference: string; shopProductId: string; productTitle: string; quantity: number
  commissionType: string; commissionValue: number; commissionBase: number; calculatedAmount: number
  reversedAmount: number; reservedAmount: number; paidAmount: number; remainingAmount: number
  currencyCode: string; lifecycleStatus: string; riskStatus: string; deliveredAt?: string | null
  availableAt?: string | null; createdAt: string
}
export interface AffiliateOrder {
  attributionId: string; orderReference: string; orderedAt: string; source: string
  referralCode: string; riskStatus: string; commissionAmount: number; reversedAmount: number
  currency: string; items: Array<{ productId: string; title: string; quantity: number; commission: number
    reversed: number; lifecycleStatus: string; riskStatus: string }>
}
export interface AffiliatePayoutAccount {
  id: string; version: number; beneficiaryName: string; payoutMethod: 'BANK_TRANSFER' | 'PAYPAL'
  maskedDestination: string; bankCountry?: string | null
  currency: string; verificationStatus: string; active: boolean; changedAt: string; usableAfter: string
}
export interface AffiliatePayout {
  id: string; payoutNumber: string; grossAmount: number; withholdingTax: number; affiliateBorneFee: number
  netPaid: number; currency: string; status: string; beneficiary: string
  payoutMethod: 'BANK_TRANSFER' | 'PAYPAL'; maskedDestination: string
  bankReference?: string | null; reason?: string | null; requestedAt: string
  approvedAt?: string | null; sentAt?: string | null; completedAt?: string | null
}
export interface AffiliateDispute {
  id: string; disputeType: string; attributionReference?: string | null; reason: string
  evidenceReferences: string[]; status: string; submittedAt: string; reviewedAt?: string | null
  resolution?: string | null
}

export function useAffiliateAPI() {
  const application = () => apiGet<AffiliateApplication | null>('/api/app/v1/affiliate/application')
  const submitApplication = (body: AffiliateApplicationInput) => apiPost<AffiliateApplication>('/api/app/v1/affiliate/applications', body)
  const updateApplication = (body: AffiliateApplicationInput) => apiPut<AffiliateApplication>('/api/app/v1/affiliate/application', body)
  const withdrawApplication = () => apiPost<AffiliateApplication>('/api/app/v1/affiliate/application/withdraw')
  const profile = () => apiGet<AffiliateProfile | null>('/api/app/v1/affiliate/profile')
  const dashboard = () => apiGet<AffiliateDashboard>('/api/app/v1/affiliate/dashboard')
  const product = (productId: string) => apiGet<AffiliateProduct>(`/api/app/v1/affiliate/products/${productId}`)
  const orders = (page = 0, size = 20) => apiGet<PageResult<AffiliateOrder>>(`/api/app/v1/affiliate/orders?${buildQueryParams({ page, size })}`)
  const commissions = (page = 0, size = 20) => apiGet<PageResult<AffiliateCommission>>(`/api/app/v1/affiliate/commissions?${buildQueryParams({ page, size })}`)
  const payoutAccount = () => apiGet<AffiliatePayoutAccount | null>('/api/app/v1/affiliate/payout-account')
  const updatePayoutAccount = (body: { beneficiaryName: string; payoutMethod: 'BANK_TRANSFER' | 'PAYPAL'; iban?: string; paypalEmail?: string; taxId?: string; bankCountry?: string; currentPassword: string }) =>
    apiPut<AffiliatePayoutAccount>('/api/app/v1/affiliate/payout-account', body)
  const deactivatePayoutAccount = (body: { payoutAccountId: string; version: number; currentPassword: string }) =>
    apiDelete<void>('/api/app/v1/affiliate/payout-account', { body })
  const payouts = (page = 0, size = 20) => apiGet<PageResult<AffiliatePayout>>(`/api/app/v1/affiliate/payouts?${buildQueryParams({ page, size })}`)
  const requestPayout = (amount: number, currency: string, idempotencyKey: string) => apiPost<AffiliatePayout>('/api/app/v1/affiliate/payouts', {
    amount, currency, idempotencyKey,
  })
  const cancelPayout = (id: string) => apiPost<AffiliatePayout>(`/api/app/v1/affiliate/payouts/${id}/cancel`)
  const disputes = (page = 0, size = 20) => apiGet<PageResult<AffiliateDispute>>(`/api/app/v1/affiliate/disputes?${buildQueryParams({ page, size })}`)
  const submitDispute = (body: { disputeType: string; attributionReference?: string; reason: string; evidenceReferences: string[]; idempotencyKey: string }) =>
    apiPost<AffiliateDispute>('/api/app/v1/affiliate/disputes', body)
  const supplementDispute = (id: string, body: { reason: string; evidenceReferences: string[]; idempotencyKey: string }) =>
    apiPut<AffiliateDispute>(`/api/app/v1/affiliate/disputes/${id}`, body)
  const validateCode = (code: string) => apiPost<{ valid: boolean; displayName?: string | null }>('/api/app/v1/affiliate/validate-code', { code })
  const trackReferral = (body: { code: string; landingPath: string; consentRecordId: string; consentVersion: string;
    utmSource?: string; utmMedium?: string; utmCampaign?: string }) =>
    apiPost<{ valid: boolean; displayName?: string | null }>('/api/app/v1/affiliate/track-referral', body)
  const revokeTracking = () => apiDelete<void>('/api/app/v1/affiliate/tracking-consent')
  return { application, submitApplication, updateApplication, withdrawApplication, profile, dashboard,
    product, orders, commissions, payoutAccount, updatePayoutAccount, deactivatePayoutAccount, payouts, requestPayout,
    cancelPayout, disputes, submitDispute, supplementDispute, validateCode, trackReferral, revokeTracking }
}
